/**
 * EnableTicketingPanel — Event in a Box, Phase 1a operator UI.
 *
 * One-step "Enable ticketing" over the atomic provisioner (provision_event_ticketing):
 * pick the reward, and it creates the $0 placeholder ticket offer + ratchet award + link,
 * drift-proof. Honors the external-audit UX notes:
 *   - operator language for award kinds (Door prize drawing / $ voucher reward / Physical gift),
 *   - check-in containment shown as a SEPARATE completion step with a distinct
 *     "Open venue check-in" action (provisioning leaves public check-in OFF by design).
 * It does NOT issue tickets (that's the registration leg); this only configures the offer graph.
 */
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TicketIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { useToast } from "../ui/Toast";
import {
  provisionEventTicketing,
  getEventTicketingProvisioning,
  setEventCheckinEnabled,
  setEventRegistrationMode,
  getEventLandingPageId,
  generateInviteLink,
  revokeInviteToken,
  AWARD_KIND_LABELS,
  type AwardKind,
  type EscalationEndMode,
  type ProvisionResult,
  type RegistrationMode,
} from "../../services/eventTicketing.service";
import { supabase } from "../../services/supabase";
import type { Event } from "../../types/event.types";

interface Props {
  event: Event;
}

const NAME_PLACEHOLDER: Record<AwardKind, string> = {
  drawing: "chance at a door prize",
  voucher: "$5 Voucher",
  gift: "USLS range bag",
};

const AWARD_KINDS: { kind: AwardKind; label: string; hint: string }[] = [
  {
    kind: "drawing",
    label: AWARD_KIND_LABELS.drawing,
    hint: "A chance to win — entries drawn later. No cash value.",
  },
  {
    kind: "voucher",
    label: AWARD_KIND_LABELS.voucher,
    hint: "A fixed-dollar voucher every attendee can redeem.",
  },
  {
    kind: "gift",
    label: AWARD_KIND_LABELS.gift,
    hint: "A named physical item (e.g. a range bag). No cash value.",
  },
];

const EnableTicketingPanel: React.FC<Props> = ({ event }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data: prov, isLoading } = useQuery({
    queryKey: ["eventTicketingProvisioning", event.id],
    queryFn: () => getEventTicketingProvisioning(event.id),
  });

  // Form state (used only before/while configuring).
  // rewardEnabled=true → "Ticket + automatic upgrade reward"; false → "Ticket only"
  // ($0 admission ticket, no reward offer, manual on-site upgrade). Maps to award
  // kind 'none' at provisioning.
  const [rewardEnabled, setRewardEnabled] = useState(true);
  const [awardKind, setAwardKind] = useState<AwardKind>("drawing");
  const [awardName, setAwardName] = useState("");
  const [awardValue, setAwardValue] = useState("");
  const [endMode, setEndMode] = useState<EscalationEndMode>("end_of_day");
  // Per-eligibility override: an optional distinct reward for non-members/guests.
  const [guestEnabled, setGuestEnabled] = useState(false);
  const [guestKind, setGuestKind] = useState<AwardKind>("voucher");
  const [guestName, setGuestName] = useState("");
  const [guestValue, setGuestValue] = useState("");
  const [preview, setPreview] = useState<ProvisionResult | null>(null);
  const [busy, setBusy] = useState(false);
  // Track check-in state locally (event prop + optimistic after the toggle).
  const [checkinDisabled, setCheckinDisabled] = useState<boolean>(
    event.self_checkin_disabled !== false,
  );
  useEffect(() => {
    setCheckinDisabled(event.self_checkin_disabled !== false);
  }, [event.self_checkin_disabled]);

  // EIB Gate 5 — registration mode + per-contact invite links.
  const [regMode, setRegMode] = useState<RegistrationMode>("invite_gated");
  const [openCap, setOpenCap] = useState("");
  const [modeBusy, setModeBusy] = useState(false);
  const [landingPageId, setLandingPageId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);

  // Seed the form from an existing (editable) provisioning row.
  useEffect(() => {
    if (prov && prov.status === "configured") {
      const isTicketOnly = prov.award_kind === "none";
      setRewardEnabled(!isTicketOnly);
      // Keep a valid award kind in state (for when the operator re-enables a
      // reward); 'none' is not an award TYPE.
      setAwardKind(isTicketOnly ? "voucher" : (prov.award_kind as AwardKind));
      setAwardName(prov.award_name ?? "");
      setAwardValue(prov.award_value !== null ? String(prov.award_value) : "");
      // Seed the guest override from the POINTER (not the derived kind) so a failed derive can't
      // silently clear the tier on the next save: toggle stays on, and empty fields make save fail
      // validation ("give the non-member reward a name") rather than sending a clear.
      if (prov.guest_award_offer_id) {
        setGuestEnabled(true);
        if (prov.guest_award_kind) {
          setGuestKind(prov.guest_award_kind);
          setGuestName(prov.guest_award_name ?? "");
          setGuestValue(
            prov.guest_award_value ? String(prov.guest_award_value) : "",
          );
        }
      } else {
        setGuestEnabled(false);
      }
    }
    if (prov) {
      setRegMode(prov.registration_mode ?? "invite_gated");
      setOpenCap(
        prov.open_issuance_cap !== null ? String(prov.open_issuance_cap) : "",
      );
    }
  }, [prov]);

  // Resolve the event's published landing page (for building invite links).
  useEffect(() => {
    let alive = true;
    if (prov) {
      getEventLandingPageId(event.id)
        .then((id) => alive && setLandingPageId(id))
        .catch(() => alive && setLandingPageId(null));
    }
    return () => {
      alive = false;
    };
  }, [prov, event.id]);

  // A shown invite link belongs to a specific contact + invite_gated context; clear it when the
  // target email or the mode changes so a stale link is never displayed for the wrong contact.
  useEffect(() => {
    setInviteLink(null);
  }, [regMode, inviteEmail]);

  const locked = prov?.status === "locked";
  const configured = prov?.status === "configured";

  const saveRegMode = async () => {
    setModeBusy(true);
    try {
      let cap: number | null = null;
      if (regMode === "open") {
        const n = Number(openCap);
        // Require a whole positive number — reject NaN, decimals (no silent floor), 0/negative, huge.
        if (!Number.isInteger(n) || n <= 0 || n > 1_000_000) {
          showToast(
            "error",
            "Open mode needs a whole-number ticket cap between 1 and 1,000,000.",
          );
          return;
        }
        cap = n;
      }
      await setEventRegistrationMode(event.id, regMode, cap);
      await queryClient.invalidateQueries({
        queryKey: ["eventTicketingProvisioning", event.id],
      });
      showToast("success", "Registration mode saved.");
    } catch (e) {
      showToast("error", (e as Error).message || "Couldn't save mode.");
    } finally {
      setModeBusy(false);
    }
  };

  const makeInviteLink = async () => {
    setInviteBusy(true);
    setInviteLink(null);
    try {
      const email = inviteEmail.trim().toLowerCase();
      if (!email) {
        showToast("error", "Enter the invitee's email.");
        return;
      }
      if (!landingPageId) {
        showToast(
          "error",
          "Publish a registration landing page for this event first.",
        );
        return;
      }
      const { data: c } = await supabase
        .from("contacts")
        .select("id")
        .eq("email", email)
        .maybeSingle();
      if (!c?.id) {
        showToast("error", "No contact found with that email.");
        return;
      }
      const res = await generateInviteLink(
        event.id,
        c.id as string,
        landingPageId,
      );
      if (res.url) {
        setInviteLink(res.url);
        await navigator.clipboard?.writeText(res.url).catch(() => {});
        showToast("success", "Invite link generated and copied.");
      } else if (res.reused) {
        showToast(
          "error",
          res.status === "consumed"
            ? "This contact already registered with their invite (the link was used). Revoke to issue a new one."
            : "This contact already has a live invite link. Revoke it to generate a new one.",
        );
      } else {
        showToast("error", res.error || "Couldn't generate the link.");
      }
    } catch (e) {
      showToast("error", (e as Error).message || "Couldn't generate the link.");
    } finally {
      setInviteBusy(false);
    }
  };

  const revokeInvite = async () => {
    setInviteBusy(true);
    try {
      const email = inviteEmail.trim().toLowerCase();
      const { data: c } = await supabase
        .from("contacts")
        .select("id")
        .eq("email", email)
        .maybeSingle();
      if (!c?.id) {
        showToast("error", "No contact found with that email.");
        return;
      }
      const res = await revokeInviteToken(event.id, c.id as string);
      setInviteLink(null);
      let reasonMsg = res.reason || "Nothing to revoke.";
      if (res.reason === "ticket_still_active") {
        reasonMsg =
          "This contact still has a live ticket — void it first, then revoke to re-issue their invite.";
      } else if (res.reason === "no_active_token") {
        reasonMsg = "No invite to revoke for this contact.";
      }
      showToast(
        res.ok ? "success" : "error",
        res.ok ? "Invite revoked. You can generate a new one." : reasonMsg,
      );
    } catch (e) {
      showToast("error", (e as Error).message || "Couldn't revoke.");
    } finally {
      setInviteBusy(false);
    }
  };

  function validate(): string | null {
    // Ticket-only: no reward to validate.
    if (!rewardEnabled) {
      return null;
    }
    if (!awardName.trim()) {
      return "Give the reward a name.";
    }
    if (awardKind === "voucher") {
      const v = Number(awardValue);
      if (!Number.isFinite(v) || v <= 0) {
        return "Enter a dollar amount above $0.";
      }
      if (Math.round(v * 100) !== v * 100) {
        return "Dollar amount can have at most 2 decimals.";
      }
    }
    if (guestEnabled) {
      if (!guestName.trim()) {
        return "Give the non-member reward a name (or turn it off).";
      }
      if (guestKind === "voucher") {
        const gv = Number(guestValue);
        if (!Number.isFinite(gv) || gv <= 0) {
          return "Enter a non-member voucher amount above $0.";
        }
        if (Math.round(gv * 100) !== gv * 100) {
          return "Non-member amount can have at most 2 decimals.";
        }
      }
    }
    return null;
  }

  async function run(dryRun: boolean) {
    const err = validate();
    if (err) {
      showToast("error", err);
      return;
    }
    setBusy(true);
    try {
      const res = await provisionEventTicketing({
        eventId: event.id,
        // Ticket-only ⇒ award kind 'none' (no reward offer, no guest tier); the
        // $0 admission ticket is issued without auto-escalation.
        awardKind: rewardEnabled ? awardKind : "none",
        awardName: rewardEnabled ? awardName.trim() : null,
        awardValue:
          rewardEnabled && awardKind === "voucher" ? Number(awardValue) : null,
        escalationEndMode: endMode,
        // Guest override: send it when enabled; null clears the tier (single-tier).
        guestAwardKind: rewardEnabled && guestEnabled ? guestKind : null,
        guestAwardName: rewardEnabled && guestEnabled ? guestName.trim() : null,
        guestAwardValue:
          rewardEnabled && guestEnabled && guestKind === "voucher"
            ? Number(guestValue)
            : null,
        dryRun,
      });
      if (dryRun) {
        setPreview(res);
      } else if (res.blocked) {
        showToast(
          "error",
          res.reason === "event_already_has_issued_tickets"
            ? "This event already has issued tickets and can't be set up through Event in a Box."
            : "Couldn't enable ticketing.",
        );
      } else {
        setPreview(null);
        showToast("success", "Ticket rewards are configured.");
        queryClient.invalidateQueries({
          queryKey: ["eventTicketingProvisioning", event.id],
        });
      }
    } catch (e) {
      showToast("error", (e as Error).message || "Couldn't enable ticketing.");
    } finally {
      setBusy(false);
    }
  }

  async function openCheckin() {
    setBusy(true);
    try {
      await setEventCheckinEnabled(event.id, true);
      setCheckinDisabled(false);
      showToast("success", "Public venue check-in is now open.");
    } catch (e) {
      showToast("error", (e as Error).message || "Couldn't open check-in.");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-1">
        <TicketIcon className="h-5 w-5 text-primary-600" />
        <h3 className="text-base font-semibold text-gray-900">
          Event Ticketing
        </h3>
        {locked && (
          <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            <LockClosedIcon className="h-3 w-3" /> Locked
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Set up a ticket with a check-in reward in one step. This configures the
        reward only — it doesn&apos;t send any tickets.
      </p>

      {/* LOCKED: tickets issued, contract immutable. */}
      {locked &&
        (prov!.award_kind === "none" ? (
          <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
            Tickets have already been issued for this event, so the setup is
            locked and can&apos;t be changed here.{" "}
            <strong>Ticket only</strong> — no automatic upgrade reward (staff can
            upgrade tickets manually on this page).
          </div>
        ) : (
          <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
            Tickets have already been issued for this event, so the reward setup
            is locked and can&apos;t be changed here. Reward:{" "}
            <strong>{AWARD_KIND_LABELS[prov!.award_kind as AwardKind]}</strong> —{" "}
            {prov!.award_name}
            {prov!.award_value ? ` ($${prov!.award_value})` : ""}.
          </div>
        ))}

      {/* CONFIGURE (new or editable) */}
      {!locked && (
        <div className="space-y-5">
          {/* Top-level choice: ticket only vs ticket + automatic upgrade reward */}
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">
              Ticket setup
            </legend>
            <div className="space-y-2">
              <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer border-gray-200">
                <input
                  type="radio"
                  name="rewardMode"
                  className="mt-0.5"
                  checked={rewardEnabled}
                  onChange={() => setRewardEnabled(true)}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Ticket + automatic upgrade reward
                  </span>
                  <span className="block text-xs text-gray-500">
                    The admission ticket upgrades to a reward at check-in
                    (configure it below).
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer border-gray-200">
                <input
                  type="radio"
                  name="rewardMode"
                  className="mt-0.5"
                  checked={!rewardEnabled}
                  onChange={() => setRewardEnabled(false)}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Ticket only — no upgrade reward
                  </span>
                  <span className="block text-xs text-gray-500">
                    A $0 admission ticket, no automatic reward. Staff upgrade
                    tickets manually on this page (+$20).
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          {rewardEnabled ? (
            <>
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">
              Check-in reward
            </legend>
            <div className="space-y-2">
              {AWARD_KINDS.map(({ kind, label, hint }) => (
                <label
                  key={kind}
                  className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer ${
                    awardKind === kind
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="awardKind"
                    className="mt-0.5"
                    checked={awardKind === kind}
                    onChange={() => setAwardKind(kind)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      {label}
                    </span>
                    <span className="block text-xs text-gray-500">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reward name (shown to guests)
              </label>
              <input
                type="text"
                value={awardName}
                onChange={(e) => setAwardName(e.target.value)}
                placeholder={NAME_PLACEHOLDER[awardKind]}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            {awardKind === "voucher" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Voucher amount ($)
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={awardValue}
                  onChange={(e) => setAwardValue(e.target.value)}
                  placeholder="5.00"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reward available until
              </label>
              <select
                value={endMode}
                onChange={(e) =>
                  setEndMode(e.target.value as EscalationEndMode)
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="end_of_day">End of the event day</option>
                <option value="fixed" disabled>
                  A specific time (coming soon)
                </option>
              </select>
            </div>
          </div>

          {/* Per-eligibility override: optional distinct reward for non-members/guests */}
          <fieldset className="border-t border-gray-100 pt-4">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={guestEnabled}
                onChange={(e) => setGuestEnabled(e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium">
                  Different reward for non-members
                </span>{" "}
                — members get the reward above; a non-member or guest gets this
                one instead (e.g. a join-to-unlock offer). Which one applies is
                decided at check-in.
              </span>
            </label>
            {guestEnabled && (
              <div className="mt-3 space-y-3 rounded-md border border-gray-200 p-3">
                <div className="space-y-2">
                  {AWARD_KINDS.map(({ kind, label, hint }) => (
                    <label
                      key={kind}
                      className={`flex items-start gap-3 rounded-md border p-2 cursor-pointer ${
                        guestKind === kind
                          ? "border-primary-500 bg-primary-50"
                          : "border-gray-200"
                      }`}
                    >
                      <input
                        type="radio"
                        name="guestAwardKind"
                        className="mt-0.5"
                        checked={guestKind === kind}
                        onChange={() => setGuestKind(kind)}
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">
                          {label}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {hint}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Non-member reward name
                    </label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder={NAME_PLACEHOLDER[guestKind]}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  {guestKind === "voucher" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Non-member voucher amount ($)
                      </label>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={guestValue}
                        onChange={(e) => setGuestValue(e.target.value)}
                        placeholder="5.00"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </fieldset>
            </>
          ) : (
            <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
              <span className="font-medium text-gray-900">Ticket only</span> — a
              $0 admission ticket is issued with no automatic upgrade reward.
              Staff can upgrade tickets manually on this page after check-in.
            </div>
          )}

          {preview && !preview.blocked && rewardEnabled && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
              <div className="font-medium mb-0.5">Preview</div>
              This will set up a ticket whose reward is{" "}
              <strong>{AWARD_KIND_LABELS[awardKind]}</strong>
              {awardKind === "voucher"
                ? ` ($${Number(awardValue).toFixed(2)})`
                : ""}{" "}
              — “{awardName}”. It writes nothing to guests. Public check-in
              stays off until you open it below.
            </div>
          )}
          {preview?.blocked && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              This event already has issued tickets and can&apos;t be set up
              through Event in a Box.
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => run(true)}
              disabled={busy}
            >
              Preview
            </Button>
            <Button onClick={() => run(false)} disabled={busy}>
              {configured ? "Update reward setup" : "Enable ticketing"}
            </Button>
          </div>
        </div>
      )}

      {/* CHECK-IN CONTAINMENT — separate completion step (auditor UX note) */}
      {(configured || locked) && (
        <div className="mt-5 border-t border-gray-100 pt-4">
          {checkinDisabled ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
              <div className="text-sm font-medium text-amber-900">
                Ticket rewards are configured. Public check-in is still off.
              </div>
              <p className="text-xs text-amber-800 mt-1 mb-3">
                For safety, guests can&apos;t check in until you deliberately
                open it — do this when the venue is ready.
              </p>
              <Button onClick={openCheckin} disabled={busy}>
                Open venue check-in
              </Button>
            </div>
          ) : (
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
              Public venue check-in is <strong>open</strong>. Guests can check
              in.
              <button
                type="button"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await setEventCheckinEnabled(event.id, false);
                    setCheckinDisabled(true);
                    showToast("success", "Public check-in closed.");
                  } catch (e) {
                    showToast(
                      "error",
                      (e as Error).message || "Couldn't close check-in.",
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                className="ml-2 text-xs text-green-700 underline hover:text-green-900"
              >
                Close it
              </button>
            </div>
          )}
        </div>
      )}

      {/* EIB Gate 5 — Registration mode & per-contact invites */}
      {(configured || locked) && (
        <div className="mt-5 border-t border-gray-100 pt-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">
            Registration &amp; invites
          </h4>
          {/* Code-audit MED: a show-up event has no registration step at all, so
              the stored registration mode below is inert. Say so, rather than
              letting the panel read as "invite-gated registration". */}
          {(prov as { workflow_mode?: string | null } | null)?.workflow_mode ===
            "show_up" && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
              This event was set up as a <strong>show-up</strong> event: nobody
              registers in advance, so the registration setting below is not
              used. Members identify themselves at the venue through self
              check-in.
            </div>
          )}
          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name={`regmode-${event.id}`}
                checked={regMode === "invite_gated"}
                onChange={() => setRegMode("invite_gated")}
                className="mt-0.5"
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium">Invite only</span> — a reward
                ticket mints only for a contact who registers with their own
                invite link (default).
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name={`regmode-${event.id}`}
                checked={regMode === "open"}
                onChange={() => setRegMode("open")}
                className="mt-0.5"
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium">Open</span> — anyone with the link
                can register, capped at a total number of tickets.
              </span>
            </label>
            {regMode === "open" && (
              <div className="ml-6">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Total ticket cap
                </label>
                <input
                  type="number"
                  min={1}
                  value={openCap}
                  onChange={(e) => setOpenCap(e.target.value)}
                  className="w-32 rounded border border-gray-300 px-2 py-1 text-sm"
                  placeholder="e.g. 200"
                />
                {prov &&
                  Number.isInteger(Number(openCap)) &&
                  Number(openCap) > 0 &&
                  Number(openCap) < prov.issued_count && (
                    <p className="mt-1 text-xs text-amber-700">
                      Heads up: {Number(openCap)} is below the{" "}
                      {prov.issued_count} ticket
                      {prov.issued_count === 1 ? "" : "s"} already issued — no
                      new tickets will mint until voids bring the live count
                      under the cap.
                    </p>
                  )}
              </div>
            )}
            <Button onClick={saveRegMode} disabled={modeBusy}>
              Save registration mode
            </Button>
          </div>

          {regMode === "invite_gated" && (
            <div className="rounded-md bg-gray-50 border border-gray-200 p-3 space-y-2">
              <div className="text-xs font-medium text-gray-600">
                Generate an invite link for a contact
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="invitee@email.com"
                  className="flex-1 min-w-[12rem] rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <Button onClick={makeInviteLink} disabled={inviteBusy}>
                  Generate link
                </Button>
                <button
                  type="button"
                  onClick={revokeInvite}
                  disabled={inviteBusy}
                  className="text-xs text-red-600 underline hover:text-red-800"
                >
                  Revoke
                </button>
              </div>
              {inviteLink && (
                <div className="rounded bg-white border border-gray-200 p-2 text-xs break-all text-gray-700">
                  <span className="font-medium text-gray-500">Copied — </span>
                  {inviteLink}
                </div>
              )}
              <p className="text-[11px] text-gray-400">
                The link is shown once and copied to your clipboard; it
                can&apos;t be retrieved later. To reissue, Revoke then Generate.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnableTicketingPanel;
